<template>
  <Toast />
  <ConfirmDialog group="headless">
    <template #container="{ message, acceptCallback, rejectCallback }">
      <div class="flex flex-col items-center p-8 bg-surface-0 dark:bg-surface-900 rounded">
        <div
          class="rounded-full bg-white outline outline-black inline-flex justify-center items-center h-24 w-24 -mt-20">
          <i class="pi pi-question text-5xl"></i>
        </div>
        <span class="font-bold text-2xl block mb-2 mt-6">{{ message.header }}</span>
        <p class="mb-0">{{ message.message }}</p>
        <div class="flex items-center gap-2 mt-6">
          <Button :label="message.rejectLabel" outlined @click="rejectCallback" class="w-32"></Button>
          <Button :label="message.acceptLabel" @click="acceptCallback" class="w-32"></Button>
        </div>
      </div>
    </template>
  </ConfirmDialog>
  <ModalMarketing v-if="modalShowMarketing" @closeModal="closeModal" :marketing="selectedMarketing" />
  <IndexView />
  <div id="overlay" class="fixed inset-0 bg-black bg-opacity-30 z-30" :class="{ 'hidden': !isBlur }"></div>
  <ViewContainer>
    <section class="p-6">
      <PageTitle title="Staff" />
      <div class="border-t-2 border-primary bg-white dark:bg-dark-900 w-full rounded-lg py-6 px-5 shadow-lg shadow-light-100 dark:shadow-dark-500">
        <div id="table-header" class="mb-5 flex justify-between">
          <AddButton to_url="#" button_text="+ Tambah Staff" button_class="text-base py-2 font-medium"
            @click="showModal" />
        </div>
        <div class="overflow-x-auto">
          <div class="inline-block min-w-full align-middle">
            <div class="overflow-hidden shadow-lg rounded-lg">
              <TableComponent>
                <TableHead>
                  <TableRow>
                    <TableHeadCell>No</TableHeadCell>
                    <TableHeadCell>Nama Staff</TableHeadCell>
                    <TableHeadCell>No. HP</TableHeadCell>
                    <TableHeadCell>Email</TableHeadCell>
                    <TableHeadCell>Alamat</TableHeadCell>
                    <TableHeadCell>Action</TableHeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow v-if="allMarketings.length == 0">
                    <TableCell colspan="6" customClass="text-center italic text-gray-400 dark:text-gray-500"
                      noTextColor>
                      Belum ada data
                    </TableCell>
                  </TableRow>
                  <TableRow v-else v-for="(marketing, index) in allMarketings" :key="marketing.id" :index="index"
                    alternateRow isBody>
                    <TableCell>{{ index + 1 }}</TableCell>
                    <TableCell>{{ marketing.name }}</TableCell>
                    <TableCell>{{ marketing.phone }}</TableCell>
                    <TableCell>{{ marketing.email }}</TableCell>
                    <TableCell>{{ marketing.address }}</TableCell>
                    <TableCell noTextColor customClass="text-gray-500 dark:text-gray-400 flex">
                      <button :id="'edit-data-' + marketing.id" @click="editMarketing(marketing)"
                        class="bg-warning hover:bg-hover-warning flex justify-center items-center mx-1 p-2 rounded-md group">
                        <PencilSquareIcon class="h-4 w-4 text-light-900 group-hover:text-primary" />
                      </button>
                      <button :id="'delete-data-' + marketing.id" @click="deleteMarketing(marketing)"
                        class="bg-danger hover:bg-hover-danger flex justify-center items-center mx-1 p-2 rounded-md group">
                        <TrashIcon class="h-4 w-4 text-white group-hover:text-light-50" />
                      </button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </TableComponent>
              <PaginationButtons v-model="currentPage" :totalPages="totalPages" />
            </div>
          </div>
        </div>
      </div>
    </section>
  </ViewContainer>
</template>

<script>
import IndexView from '@/views/IndexView.vue';
import PageTitle from '@/components/PageTitle.vue';
import AddButton from '@/components/buttons/AddButton.vue';
import ModalMarketing from '@/components/modals/ModalMarketing.vue';
import ViewContainer from '@/components/ViewContainer.vue';
import { TableComponent, TableHead, TableRow, TableHeadCell, TableBody, TableCell } from '@/components/table';
import { PencilSquareIcon, TrashIcon } from '@heroicons/vue/24/solid';
import { mapGetters, mapActions } from 'vuex';
import PaginationButtons from '@/components/PaginationButtons.vue';
import { Button, ConfirmDialog, Toast } from 'primevue';

export default {
  name: "MarketingView",
  components: {
    IndexView, PageTitle, AddButton, ModalMarketing, ViewContainer,
    TableComponent, TableHead, TableRow, TableHeadCell, TableBody, TableCell,
    PencilSquareIcon, TrashIcon, PaginationButtons, Toast, ConfirmDialog, Button
  },
  data() {
    return {
      modalShowMarketing: false,
      isBlur: false,
      selectedMarketing: null,
      currentPage: 1,
      totalPages: 1,
    }
  },
  computed: {
    ...mapGetters('marketings', ['allMarketings'])
  },
  created() {
    this.$store.dispatch("marketings/allMarketings", { limit: 50 });
  },
  watch: {
    currentPage: {
      immediate: true,
      handler(newVal) {
        this.fetchMarketings({ page: newVal });
      },
    }
  },
  methods: {
    ...mapActions('marketings', ['deleteMarketingById']),
    async fetchMarketings(params = { page: 1, limit: 50 }) {
      if (!params.page) params.page = this.currentPage;
      if (!params.limit) params.limit = 50;

      try {
        const { pagination } = await this.$store.dispatch('marketings/fetchMarketings', params);
        if (pagination) {
          this.totalPages = pagination.totalPages;
        }
      } catch (error) {
        console.log(error);
      }
    },
    showModal() {
      this.modalShowMarketing = true;
      this.isBlur = true;
      this.selectedMarketing = null;
    },
    closeModal() {
      setTimeout(() => {
        this.modalShowMarketing = false;
        this.isBlur = false;
        this.fetchMarketings();
      }, 200);
    },
    editMarketing(marketing) {
      this.selectedMarketing = marketing;
      this.modalShowMarketing = true;
      this.isBlur = true;
    },
    deleteMarketing(marketing) {
      this.$confirm.require({
        group: 'headless',
        header: 'Apakah anda yakin ingin menghapus marketing?',
        message: 'Silahkan konfirmasi sebelum diproses.',
        acceptLabel: 'Ya',
        rejectLabel: 'Tidak',
        accept: async () => {
          try {
            await this.deleteMarketingById(marketing.id);
            this.fetchMarketings();
            this.$toast.add({ severity: 'success', summary: 'Success', detail: 'Berhasil menghapus marketing', life: 3000 });
          }
          catch (error) {
            console.error('Gagal menghapus marketing', error);
            this.$toast.add({ severity: 'error', summary: 'Failed', detail: 'Gagal menghapus marketing', life: 3000 });
          }
        },
        reject: () => {
          this.$toast.add({ severity: 'error', summary: 'Rejected', detail: 'Batal menghapus marketing', life: 3000 });
        }
      });
    },
  },
}
</script>
